import { ServerCommandError } from '@/infrastructure/supabase/client'
import type { HelpRequestCommand, MissionRepository } from '../MissionRepository'

/** Stockage clé-valeur minimal (localStorage dans le navigateur, mémoire dans les tests). */
export interface KeyValueStore {
  get: (key: string) => string | null
  set: (key: string, value: string) => void
}

export const browserKeyValue: KeyValueStore = {
  get: (key) => {
    try {
      return window.localStorage.getItem(key)
    } catch {
      return null
    }
  },
  set: (key, value) => {
    try {
      window.localStorage.setItem(key, value)
    } catch {
      // stockage indisponible : la demande reste en mémoire pour cette page
    }
  },
}

export interface PendingHelp extends HelpRequestCommand {
  createdAt: string
}

/**
 * Demandes d'aide pas encore reçues par le serveur, par compte. L'identifiant est créé par la tablette : un renvoi
 * (réseau revenu, rechargement) ne crée jamais de doublon. « Envoyée » n'est affiché qu'après la réponse du serveur.
 */
export class HelpOutbox {
  private memory: PendingHelp[] | null = null

  constructor(
    private readonly userId: string,
    private readonly storage: KeyValueStore = browserKeyValue,
  ) {}

  private get key(): string {
    return `reca-operator-v3-help-outbox:${this.userId}`
  }

  pending(): PendingHelp[] {
    if (this.memory) return this.memory
    try {
      const raw = this.storage.get(this.key)
      this.memory = raw ? (JSON.parse(raw) as PendingHelp[]) : []
    } catch {
      this.memory = []
    }
    return this.memory
  }

  private write(next: PendingHelp[]): void {
    this.memory = next
    this.storage.set(this.key, JSON.stringify(next))
  }

  add(request: PendingHelp): void {
    this.write([...this.pending().filter((candidate) => candidate.id !== request.id), request])
  }

  /** Envoie tout ce qui attend. Retourne les demandes reçues et les codes des refus définitifs (retirés de la file). */
  async flush(repository: Pick<MissionRepository, 'sendHelpRequest'>): Promise<{ sent: string[]; refused: string[] }> {
    const sent: string[] = []
    const refused: string[] = []
    const settled = new Set<string>()
    for (const request of this.pending()) {
      const command: HelpRequestCommand = {
        id: request.id,
        reason: request.reason,
        ...(request.comment ? { comment: request.comment } : {}),
        ...(request.position ? { position: request.position } : {}),
      }
      try {
        await repository.sendHelpRequest(command)
        sent.push(command.id)
        settled.add(command.id)
      } catch (error) {
        // Refus métier : la demande ne passera jamais telle quelle. Réseau ou réponse perdue : elle attend.
        if (error instanceof ServerCommandError) {
          refused.push(error.code)
          settled.add(command.id)
        }
      }
    }
    if (settled.size > 0) this.write(this.pending().filter((request) => !settled.has(request.id)))
    return { sent, refused }
  }
}
